import * as React from 'react';
import {
  IAddonsProps,
  IInputProps,
  IInputSearchProps,
  IProps,
} from '../common/props';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import {
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { InputAddons } from '../input-addons/InputAddons';
import { inputFieldSeachOnKeyDown } from '../common/utils';

export interface IFormattedInputBaseProps<VALUE>
  extends IProps,
    IInputProps,
    IAddonsProps {
  /**
   * Whether the input is non-interactive.
   * Note that `rightElement` must be disabled separately; this prop will not affect it.
   *
   * @default false
   */
  disabled?: boolean;
  readOnly?: boolean;
  /**
   * Placeholder text in the absence of any value.
   */
  placeholder?: string;
  /**
   * Change event handler. Use `event.target.value` for new value.
   */
  onChange?: (value: VALUE | undefined) => void;
  onBlur?: (value: VALUE | undefined) => void;
  onFocus?: (e: SyntheticEvent<any>) => void;
  /**
   * Pokud je true, lze zadávat pouze kladná čísla a 0.
   */
  onlyPositive?: boolean;
  /**
   * Form value of the input, for controlled usage.
   */
  value?: VALUE;
  /**
   * Má se hodnota zarovnávat doprava? - typicky např. pro měnu.
   */
  alignRight?: boolean;
  rightAction?: ReactNode;
  rightIcon?: ReactNode;
  inputRef?: React.MutableRefObject<HTMLInputElement | undefined>;
}

export type ValueAndCursor = {
  value: string;
  cursorIndex?: number;
};

export interface IFormattedInputProps<VALUE>
  extends IFormattedInputBaseProps<VALUE>,
    IInputSearchProps {
  formatViewValue: (value: VALUE) => string;
  unifyEditedValue: (valueAndCursor: ValueAndCursor) => ValueAndCursor;
  isValidInput: (value: string) => boolean;
  isValidValue: (value: string) => boolean;
  parseValue: (value: string) => VALUE | undefined;
  compareValues?: (value1?: VALUE, value2?: VALUE) => boolean;
  autoFocus?: boolean;
}

export type FormattedInputProps<VALUE> = IFormattedInputProps<VALUE> & {
  readOnly?: boolean;
};

type CursorIndex = {
  startIndex: number;
  endIndex: number;
};

/**
 * Komponenta pro editaci obsahu, který se po opuštění pole případně naformátuje/opraví (např. čísla).
 */
export const FormattedInput = <VALUE extends unknown>({
  className,
  onChange,
  onFocus,
  compareValues = (value1, value2) => value1 === value2,
  onBlur,
  onSearch,
  value,
  onlyPositive,
  alignRight,
  formatViewValue,
  addonAfter,
  isValidInput,
  isValidValue,
  parseValue,
  addonAfterCompact,
  unifyEditedValue,
  rightAction,
  rightIcon,
  disabled,
  dataTest,
  readOnly,
  autoFocus,
  inputRef: externalInputRef,
  ...other
}: FormattedInputProps<VALUE>) => {
  // Sjednocení vstupní hodnoty na hodnotu nebo undefined, prázdný string se zkonvertuje na undefined
  const updateValue =
    value !== undefined && value !== null && `${value}` !== ''
      ? value
      : undefined;

  // Je focus v inputu
  const [hasFocus, setHasFocus] = useState<boolean>(false);

  // Pro uchování předchozí hodnoty - pokud se vstupní změní (např. pomocí exterí změny ve formuláři), chceme aktualizovat tu co editujeme
  const prevValue = useRef<VALUE | undefined>();

  // Pokud zadaná hodnota neodpovídá patternu, tak se nastaví tento příznak - hodnota zčervená a při opuštění pole se vymažě
  const [error, setError] = useState<boolean>(false);

  // Nastavení kurzoru na požadovanou pozici, pokud se změní cursorIndex
  const [cursorIndex, setCursorIndex] = useState<CursorIndex | undefined>();
  const internalInputRef = useRef<any>();
  const inputRef: React.MutableRefObject<HTMLInputElement> =
    externalInputRef || internalInputRef;
  useEffect(() => {
    if (cursorIndex) {
      inputRef.current.selectionStart = cursorIndex.startIndex;
      inputRef.current.selectionEnd = cursorIndex.endIndex;
    }
  }, [cursorIndex]);

  // ---

  const lastInteractionType = useRef<'mouse' | 'keyboard' | undefined>(
    undefined
  );

  useEffect(() => {
    const onMouseDown = () => {
      lastInteractionType.current = 'mouse';
    };
    const onKeyDown = () => {
      lastInteractionType.current = 'keyboard';
    };

    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Konverze hodnoty na view hodnotu pokud není focus v prvku
  const valueToViewStr = (value: VALUE | undefined): string => {
    if (value !== undefined) {
      return formatViewValue(value);
    } else {
      return '';
    }
  };

  // Konverze hodnoty do string hodnoty do inputu
  const valueToUnifiedEditingStr = (value: VALUE | undefined): string => {
    if (value !== undefined) {
      const textValue = formatViewValue(value);
      return unifyEditedValue({ value: textValue }).value;
    } else {
      return '';
    }
  };

  // Editovaný text v inputu, pokud je focus v inputu a edituje se, tak nechceme přeformátovávat ze vstupu
  const [text, setText] = useState(valueToUnifiedEditingStr(updateValue));

  let renderValue = text;
  // console.log(
  //   'CCCC',
  //   prevValue.current,
  //   updateValue,
  //   compareValues(prevValue.current, updateValue),
  //   prevValue.current === updateValue
  // );
  if (!compareValues(prevValue.current, updateValue)) {
    renderValue = valueToUnifiedEditingStr(updateValue);
    setText(renderValue);
  }
  prevValue.current = updateValue;

  const handleChange = useCallback(
    (e: SyntheticEvent<any>) => {
      // @ts-ignore
      let inputValue = unifyEditedValue({ value: e.target.value }).value;

      if (!inputValue) {
        setText('');
        prevValue.current = undefined;
        onChange?.(undefined);
      } else {
        if (isValidInput(inputValue)) {
          setText(inputValue);
          setCursorIndex({
            // @ts-ignore
            startIndex: e.target?.selectionStart,
            // @ts-ignore
            endIndex: e.target?.selectionEnd,
          });

          // Error + onchange
          if (!isValidValue(inputValue)) {
            setError(true);

            // Chybná hodnota, dáme jako undefined
            prevValue.current = undefined;
            onChange?.(undefined);
          } else {
            setError(false);

            // On change
            const parsedValue = parseValue(inputValue);
            if (parsedValue !== updateValue) {
              prevValue.current = parsedValue;
              onChange?.(parsedValue);
            }
          }
        } else {
          setCursorIndex({
            // @ts-ignore
            startIndex: e.target?.selectionStart - 1,
            // @ts-ignore
            endIndex: e.target?.selectionEnd - 1,
          });
        }
      }
    },
    [updateValue, parseValue, isValidValue, isValidInput]
  );

  const handleFocus = useCallback(
    (e: SyntheticEvent<any>) => {
      const inputElement = e.target as HTMLInputElement;
      setTimeout(() => {
        const cursorStart = inputElement.selectionStart;

        setHasFocus(true);

        // Ta co je aktuálně vidět
        if (updateValue !== undefined) {
          const viewValue = formatViewValue(updateValue);

          let valueAndCursor = unifyEditedValue({
            value: viewValue,
            cursorIndex: cursorStart ?? undefined,
          });
          let inputValue = valueAndCursor.value;
          setText(inputValue);

          // Při kliknutí myší na pozici kurzoru tam kurzor dát, při focusu z klávesnice označit vše
          if (lastInteractionType.current === 'mouse') {
            if (valueAndCursor.cursorIndex !== undefined) {
              setCursorIndex({
                startIndex: valueAndCursor.cursorIndex,
                endIndex: valueAndCursor.cursorIndex,
              });
            } else {
              setCursorIndex({
                startIndex: inputValue.length,
                endIndex: inputValue.length,
              });
            }
          } else if (lastInteractionType.current === 'keyboard') {
            setCursorIndex({
              startIndex: 0,
              endIndex: inputValue.length,
            });
          }
        }
        // Vím že to takto není ideální, event hned mimo setTimeout ale zjištění pozice kurzoru před tímto setTimeoutem nejde a onFocus se má volat až po nastavení focusu a kurzoru
        onFocus?.(e);
      }, 1);
    },
    [setHasFocus, setText, updateValue, onFocus]
  );

  const handleBlur = useCallback(() => {
    setHasFocus(false);

    if (error) {
      onBlur?.(undefined);
    } else {
      onBlur?.(updateValue);
    }
    if (error) {
      setError(false);
    }
  }, [setHasFocus, error, updateValue, onBlur]);

  if (!hasFocus) {
    renderValue = valueToViewStr(updateValue);
  }

  const input = (
    <div
      className={classNames(className, 'bp3-input-group', Classes.FIELD_INPUT, {
        [Classes.FIELD_INPUT_ALIGN_RIGHT]: alignRight,
        [Classes.FIELD_INPUT_VALUE_ERROR]: error,
        [Classes.FIELD_INPUT_HAS_RIGHT_ACTION]: !!rightAction,
        [Classes.FIELD_INPUT_HAS_RIGHT_ICON]: !!rightIcon,
      })}
    >
      <input
        data-test={dataTest ? `input-${dataTest}` : undefined}
        className={classNames('bp3-input')}
        onKeyDown={inputFieldSeachOnKeyDown(onSearch)}
        {...other}
        disabled={disabled}
        readOnly={readOnly}
        value={renderValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        autoFocus={autoFocus}
        ref={inputRef}
      />
      {(rightAction || rightIcon) && (
        <div className={Classes.FIELD_INPUT_RIGHT_ELEMENT}>
          {rightAction || rightIcon}
        </div>
      )}
    </div>
  );

  return (
    <InputAddons addonAfter={addonAfter} addonAfterCompact={addonAfterCompact}>
      {input}
    </InputAddons>
  );
};
